import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { DashTransactionsResponse } from "../ceo-dashboard.service";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface FundSubscriptionsChartProps {
  transactionsData: DashTransactionsResponse | null;
  loading: boolean;
  dateRange: [Dayjs | null, Dayjs | null];
}

const FundSubscriptionsChart: React.FC<FundSubscriptionsChartProps> = ({
  transactionsData,
  loading,
  dateRange,
}) => {
  // Default funds with their colors
  const defaultFunds = [
    { name: "Kamco Investment Fund", color: "#7086FD", borderColor: "#7086FD" },
    { name: "Kamco Islamic Fund", color: "#6FD195", borderColor: "#6FD195" },
    {
      name: "Kamco Premier Market Index Fund",
      color: "#FFAE4C",
      borderColor: "#FFAE4C",
    },
  ];

  // Generate all months in the selected range
  const generateMonthsRange = () => {
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) return [];

    const months: string[] = [];
    let current = startDate.clone();

    while (current.isSameOrBefore(endDate, "month")) {
      months.push(current.format("MMM YYYY"));
      current = current.add(1, "month");
    }

    return months;
  };

  // Process chart data for fund subscriptions using subscriptionBreakdown
  const processChartData = () => {
    if (!transactionsData?.data?.subscriptionBreakdown) return null;

    const allMonths = generateMonthsRange();
    const groupedData: { [key: string]: { [key: string]: number } } = {};

    // Initialize all months and funds with zero values
    allMonths.forEach((month) => {
      groupedData[month] = {};
      defaultFunds.forEach((fund) => {
        groupedData[month][fund.name] = 0;
      });
    });

    // Process subscriptionBreakdown data
    transactionsData.data.subscriptionBreakdown.forEach((breakdown) => {
      // Parse the month string (format: "2025-08")
      const breakdownDate = dayjs(breakdown.month + "-01"); // Add day to make it a valid date
      const chartMonth = breakdownDate.format("MMM YYYY");
      const amount = breakdown.total;
      const fundName = breakdown.fundNameEn;

      if (allMonths.includes(chartMonth)) {
        // Find matching fund with improved matching logic
        const matchingFund = defaultFunds.find((fund) => {
          const fundNameNormalized = fund.name.toLowerCase().trim();
          const breakdownNameNormalized = fundName.toLowerCase().trim();

          return (
            // Exact match
            fundNameNormalized === breakdownNameNormalized ||
            // Contains match (either direction)
            fundNameNormalized.includes(breakdownNameNormalized) ||
            breakdownNameNormalized.includes(fundNameNormalized) ||
            // Match key words (Islamic, Investment, Premier)
            (fundNameNormalized.includes('islamic') && breakdownNameNormalized.includes('islamic')) ||
            (fundNameNormalized.includes('investment') && breakdownNameNormalized.includes('investment')) ||
            (fundNameNormalized.includes('premier') && breakdownNameNormalized.includes('premier'))
          );
        });

        if (matchingFund) {
          groupedData[chartMonth][matchingFund.name] += amount;
        }
      }
    });

    const datasets = defaultFunds.map(({ name, color, borderColor }) => ({
      label: name,
      data: allMonths.map((month) => groupedData[month][name]),
      borderColor,
      backgroundColor: color + "25",
      borderWidth: 3,
      pointBackgroundColor: borderColor,
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
      tension: 0.4,
      fill: false,
    }));

    return {
      labels: allMonths,
      datasets,
    };
  };

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
        position: "bottom" as const,
        align: "center" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          font: {
            family: "Montserrat",
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${parseFloat(
              context.parsed.y.toString()
            ).toLocaleString()} KWD`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            family: "Montserrat",
            size: 11,
          },
          color: "#6b7280",
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: "rgba(0, 0, 0, 0.1)",
        },
        ticks: {
          font: {
            family: "Montserrat",
            size: 11,
          },
          color: "#6b7280",
          callback: function (value) {
            return parseFloat(value.toString()).toLocaleString() + "K";
          },
        },
      },
    },
  };

  const processedChartData = processChartData();

  return (
    <div className="w-full lg:flex-1 inline-flex flex-col justify-start items-start shadow-md bg-white p-5 rounded-lg gap-5 lg:h-[420px] min-h-[300px]">
      <div className="self-stretch inline-flex justify-between items-center">
        <div className="justify-start text-zinc-800 text-xs font-semibold font-['Montserrat']">
          Monthly Fund Subscriptions
        </div>
      </div>
      <div className="self-stretch flex-1 bg-white flex flex-col justify-start items-start gap-2.5">
        <div className="self-stretch flex-1 relative min-h-[300px]">
          {processedChartData ? (
            <Line data={processedChartData} options={chartOptions} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {loading ? "Loading chart..." : "No data available"}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-5">
        <div className="flex items-center gap-1">
          <img src="/images/deposit-node.svg" alt="Deposit" />
          <span className="justify-start text-black/70 text-xs font-normal font-['Inter']">
            Kamco Investment Fund
          </span>
        </div>
        <div className="flex items-center gap-1">
          <img src="/images/withdraw-node.svg" alt="Deposit" />
          <span className="justify-start text-black/70 text-xs font-normal font-['Inter']">
            Kamco Islamic Fund
          </span>
        </div>
        <div className="flex items-center gap-1">
          <img src="/images/subscribtion-node.svg" alt="Subscription" />
          <span className="justify-start text-black/70 text-xs font-normal font-['Inter']">
            Kamco Premier Market Index Fund
          </span>
        </div>
      </div>
    </div>
  );
};

export default FundSubscriptionsChart;
